import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Spinner, Alert } from 'react-bootstrap';
import logoImage from '../assets/HiveMind_Logo.png';

function ResetPassword({ onBackToLogin, token: propToken }) {
    const [token, setToken] = useState(propToken || '');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // Extract token from URL if not provided as prop
    useEffect(() => {
        if (!token) {
            const urlParams = new URLSearchParams(window.location.search);
            const urlToken = urlParams.get('token') || urlParams.get('reset_token');
            if (urlToken) {
                setToken(urlToken);
            }
        }
    }, [token]);

    useEffect(() => {
        if (!token) {
            setError('Invalid or missing reset token');
        }
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!token) {
            setError('Invalid or missing reset token');
            return;
        }

        if (!newPassword) {
            setError('Please enter a new password');
            return;
        }

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            const response = await fetch('/api/auth/reset-password.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token: token,
                    new_password: newPassword,
                    new_password_confirm: confirmPassword,
                }),
            });

            const data = await response.json();

            if (data.status === 'success') {
                setSuccess(true);
                // Redirect to login after 3 seconds
                setTimeout(() => {
                    if (onBackToLogin) {
                        onBackToLogin();
                    }
                }, 3000);
            } else {
                setError(data.message || 'Failed to reset password');
            }
        } catch (err) {
            setError('Network error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="login-page-container">
                <div className="bg-blur-indigo"></div>
                <div className="bg-blur-fuchsia"></div>
                
                <Container className="py-5 login-page-content">
                    <Row className="justify-content-center">
                        <Col md={6} lg={4}>
                            <Card className="glass">
                                <Card.Body>
                                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                                        <img src={logoImage} alt="HiveMind Logo" className="login-logo" style={{ width: '90px', height: 'auto' }} />
                                    </div>
                                    <Card.Title className="text-center mb-4 login-title">Reset Password</Card.Title>
                                    <Alert variant="danger" className="mb-3">
                                        Invalid or missing reset token. Please use the link from your email.
                                    </Alert>
                                    <div className="text-center">
                                        <Button
                                            variant="link"
                                            onClick={onBackToLogin}
                                            className="login-link"
                                            style={{ padding: 0 }}
                                        >
                                            Back to Login
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <div className="login-page-container">
            {/* Background blur effects */}
            <div className="bg-blur-indigo"></div>
            <div className="bg-blur-fuchsia"></div>
            
            <Container className="py-5 login-page-content">
                <Row className="justify-content-center">
                    <Col md={6} lg={4}>
                        <Card className="glass">
                            <Card.Body>
                                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                                    <img src={logoImage} alt="HiveMind Logo" className="login-logo" style={{ width: '90px', height: 'auto' }} />
                                </div>
                                <Card.Title className="text-center mb-4 login-title">Reset Password</Card.Title>
                                <Card.Text className="text-center mb-4 login-subtitle">
                                    Enter your new password below
                                </Card.Text>
                                
                                {success ? (
                                    <Alert variant="success" className="mb-3">
                                        <Alert.Heading>Password Reset Successful</Alert.Heading>
                                        <p className="mb-0">Your password has been reset successfully. Redirecting to login...</p>
                                    </Alert>
                                ) : (
                                    <Form onSubmit={handleSubmit}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="login-label">New Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                placeholder="Enter new password (min 8 characters)"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                className="login-input"
                                                disabled={loading}
                                            />
                                        </Form.Group>
                                        
                                        <Form.Group className="mb-3">
                                            <Form.Label className="login-label">Confirm Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                placeholder="Confirm new password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                className="login-input"
                                                disabled={loading}
                                            />
                                        </Form.Group>
                                        
                                        {error && (
                                            <Alert variant="danger" className="mb-3">
                                                {error}
                                            </Alert>
                                        )}
                                        
                                        <Button
                                            variant="primary"
                                            size="lg"
                                            className="w-100"
                                            type="submit"
                                            disabled={loading}
                                            style={{
                                                background: '#6366f1',
                                                borderColor: '#6366f1'
                                            }}
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    Resetting...
                                                </>
                                            ) : (
                                                'Reset Password'
                                            )}
                                        </Button>
                                    </Form>
                                )}
                                
                                <div className="text-center mt-3">
                                    <Button
                                        variant="link"
                                        onClick={onBackToLogin}
                                        className="login-link"
                                        style={{ padding: 0 }}
                                    >
                                        Back to Login
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}

export default ResetPassword;


